import { createAsyncThunk } from "@reduxjs/toolkit";
import { statusServiceModulesApi } from "../../api/statusServiceModulesApi";
import {
  StatusLevelData,
  StatusServiceModule,
  UpdateStatusServiceModuleData,
} from "../../types/serviceModuleTypes";
import { AppDispatch, RootState } from "../store";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchStatusServiceModuleThunk = createAsyncThunk<
  StatusServiceModule,
  number,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "statusServiceModule/fetchStatusServiceModule",
  async (serviceModuleId, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<StatusServiceModule>(
      dispatch,
      "indirect",
      () => statusServiceModulesApi.getStatusServiceModule(serviceModuleId)
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to fetch status module");
    }
    return res.result as StatusServiceModule;
  }
);

export const updateStatusServiceModuleThunk = createAsyncThunk<
  void,
  { serviceModuleId: number; data: Partial<UpdateStatusServiceModuleData> },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "statusServiceModule/updateStatusServiceModule",
  async ({ serviceModuleId, data }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<StatusServiceModule>(
      dispatch,
      "direct",
      () => statusServiceModulesApi.updateStatusServiceModule(serviceModuleId, data),
      i18n.t("requestMessage.statusModuleUpdated")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to update status module");
    }
    await dispatch(fetchStatusServiceModuleThunk(serviceModuleId));
  }
);

export const createStatusLevelThunk = createAsyncThunk<
  void,
  { serviceModuleId: number; data: StatusLevelData },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "statusServiceModule/createStatusLevel",
  async ({ serviceModuleId, data }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest(
      dispatch,
      "direct",
      () => statusServiceModulesApi.createStatusLevel(serviceModuleId, data),
      i18n.t("requestMessage.statusLevelCreated")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to create status level");
    }
    await dispatch(fetchStatusServiceModuleThunk(serviceModuleId));
  }
);

export const updateStatusLevelThunk = createAsyncThunk<
  void,
  { serviceModuleId: number; statusLevelId: string; data: StatusLevelData },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "statusServiceModule/updateStatusLevel",
  async ({ serviceModuleId, statusLevelId, data }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest(
      dispatch,
      "direct",
      () => statusServiceModulesApi.updateStatusLevel(serviceModuleId, statusLevelId, data),
      i18n.t("requestMessage.statusLevelUpdated")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to update status level");
    }
    await dispatch(fetchStatusServiceModuleThunk(serviceModuleId));
  }
);

export const deleteStatusLevelThunk = createAsyncThunk<
  void,
  { serviceModuleId: number; statusLevelId: string },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "statusServiceModule/deleteStatusLevel",
  async ({ serviceModuleId, statusLevelId }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<null>(
      dispatch,
      "direct",
      () => statusServiceModulesApi.deleteStatusLevel(serviceModuleId, statusLevelId),
      i18n.t("requestMessage.statusLevelDeleted")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to delete status level");
    }
    await dispatch(fetchStatusServiceModuleThunk(serviceModuleId));
  }
);
